import type Patient from "parog-web/models/patient";
import dayjs from "dayjs";
import type {
  DmpConnectJSInsIdentityInfos,
  DmpConnectJSInsIdentityResult,
  IdentityInfos,
  InsIdentityResult,
  InsPatient
} from "parog-web/cnam/types";
import {
  DmpConnectJSInsIdentityResultCode,
  DMPConnectSex,
  InsIdentityResultCode
} from "parog-web/cnam/types";
import type DmpConnectJSAuthenticationContextAdapter from "parog-web/contexts/dmpconnect-js/authentication-context-adapter";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import { InsiAdapter } from "../insi-adapter";
import type DmpConnect from "./dmp-connect";

export default class DmpConnectJSInsiAdapter extends InsiAdapter {
  declare dmpConnect: DmpConnect;
  declare authenticationContext: DmpConnectJSAuthenticationContextAdapter;

  constructor(
    dmpConnect: DmpConnect,
    authenticationContext: DmpConnectJSAuthenticationContextAdapter
  ) {
    super();
    this.dmpConnect = dmpConnect;
    this.authenticationContext = authenticationContext;
  }

  async retrieveInsIdentity(patient: Patient): Promise<InsIdentityResult> {
    await this.dmpConnect.createInsiConnector();

    const insPatient = this._computeInsPatient(patient);

    const identityFromDmpConnectJS =
      await this.dmpConnect.getInsFromIdentityInformation(
        this.retrieveBillingNumber(),
        insPatient.birthName,
        insPatient.givenName,
        insPatient.sex,
        insPatient.birthDate,
        insPatient.birthPlace
      );

    return this._dmpConnectJsInsIdentityResultToInsIdentityResult(
      identityFromDmpConnectJS
    );
  }

  _computeInsPatient(patient: Patient): InsPatient {
    return {
      birthName: (patient.birthName || patient.lastName) as string,
      givenName: (patient.birthFirstName || patient.firstName) as string,
      sex: patient.gender == "male" ? DMPConnectSex.Male : DMPConnectSex.Female,
      birthDate: formatToISO8601Date(patient.birthday as Date),
      birthPlace:
        patient.birthMunicipalityCode &&
        !/[a-zA-Z]/.test(patient.birthMunicipalityCode) // Don't send a municipality code with a char (Corsica)
          ? patient.birthMunicipalityCode
          : ""
    };
  }

  _dmpConnectJsInsIdentityResultCodeToInsIdentityResultCode(
    dmpConnectJsIdentityResultCode: number
  ): InsIdentityResultCode {
    switch (dmpConnectJsIdentityResultCode) {
      case DmpConnectJSInsIdentityResultCode.InsIdentityResult_MultipleIdentitiesFound:
        return InsIdentityResultCode.InsIdentityResult_MultipleIdentitiesFound;
      case DmpConnectJSInsIdentityResultCode.InsIdentityResult_SingleIdentityFound:
        return InsIdentityResultCode.InsIdentityResult_SingleIdentityFound;
      case DmpConnectJSInsIdentityResultCode.InsIdentityResult_NoIdentityFound:
      default:
        return InsIdentityResultCode.InsIdentityResult_NoIdentityFound;
    }
  }

  _dmpConnectJsInsIdentityResultSexToInsIdentityResultSex(
    sex?: number
  ): string | undefined {
    switch (sex) {
      case DMPConnectSex.Male:
        return "male";
      case DMPConnectSex.Female:
        return "female";
      default:
        return undefined;
    }
  }

  _dmpConnectJsInsIdentityResultToInsIdentityResult(
    dmpConnectJSIdentityResult: DmpConnectJSInsIdentityResult
  ): InsIdentityResult {
    const identityMapper = (identity?: DmpConnectJSInsIdentityInfos) => {
      if (!identity) return undefined;
      return {
        value: identity.s_value,
        key: identity.s_key,
        oid: identity.s_oid,
        startDate: dayjs(identity.s_startDate, "YYYY-MM-DD").toDate(),
        endDate: dayjs(identity.s_endDate, "YYYY-MM-DD").toDate()
      };
    };

    const history = dmpConnectJSIdentityResult.Identity?.History
      ? dmpConnectJSIdentityResult.Identity?.History?.map(
          history => identityMapper(history) as IdentityInfos
        )
      : [];

    this.checkBirthday(dmpConnectJSIdentityResult.Identity?.s_birthDate);

    return {
      status: dmpConnectJSIdentityResult.s_status,
      result: this._dmpConnectJsInsIdentityResultCodeToInsIdentityResultCode(
        dmpConnectJSIdentityResult.i_insIdentityResult
      ),
      identity: identityMapper(dmpConnectJSIdentityResult.Identity?.Ins),
      birthName: dmpConnectJSIdentityResult.Identity?.s_birthName,
      given: dmpConnectJSIdentityResult.Identity?.s_given,
      birthGiven: dmpConnectJSIdentityResult.Identity?.s_birthGiven,
      sex: this._dmpConnectJsInsIdentityResultSexToInsIdentityResultSex(
        dmpConnectJSIdentityResult.Identity?.i_sex
      ),
      birthday: dayjs(
        dmpConnectJSIdentityResult.Identity?.s_birthDate,
        "YYYY-MM-DD"
      ).toDate(),
      birthPlace: dmpConnectJSIdentityResult.Identity?.s_birthPlace,
      history
    };
  }
}
